import { Delete } from "@mui/icons-material";
import { Grid, IconButton, TablePagination, Fab, Checkbox, Button } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import useCustomParams from "components/shared/useCustomParams";
import columnsConfig from "config/advancedTableColumnsControl.json";
import { Context } from "context/Wrapper";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Switch from '@mui/material/Switch';
import PrintInvoiceButton from "components/printInvoiceBtn";
import 'jspdf-autotable';
import { useFetchCollectionDocs } from "apis/dataSnapshots";
import DownloadInvoiceButton from "components/downloadInvoiceBtn";

function AdvancedTable({
  fromBuildingReferencesJobDetails,
  data: allData,
  collectionName = "",
  columnsRaw,
  handleDelete = false,
  rowCount = 0,
  handle2FA,
  isInactive,
  contactExtraFilters,
  tableId = collectionName,
  pageSize: propPageSize,
  hideActions = false,
  disableSorting = false,
  disableColumnMenu = false,
}) {
  const navigate = useNavigate();
  const context = useContext(Context);
  let data = allData
  if (fromBuildingReferencesJobDetails?.fromBuildingReferencesJob) {
    data = data.filter(item => item.building === fromBuildingReferencesJobDetails.buildingId);
  }



  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    return columnsConfig?.[collectionName] || {};
  });


  const [clientSortedRows, setClientSortedRows] = useState(null);

  const { params, updateParams } = useCustomParams();
  const sortField = params.get("sort") || "";
  const sortOrder = params.get("order") || "";

  const [sortModel, setSortModel] = useState(
    sortField ? [{ field: sortField, sort: sortOrder }] : []
  );

  useEffect(() => {
    if (sortField) {
      setSortModel([{ field: sortField, sort: sortOrder }]);
    } else {
      setSortModel([]);
    }
  }, [sortField, sortOrder]);

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);

    if (!newModel || newModel.length === 0) {
      setClientSortedRows(null);
      updateParams({ sort: "", order: "", page: 0 });
      return;
    }

    const { field, sort: dir } = newModel[0];

    if (field === "buildingAddress") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.buildingAddress.localeCompare(b.buildingAddress);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }

    if (field === "tenantString") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.tenantString.localeCompare(b.tenantString);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }

    if (field === "applicantString") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.applicantString.localeCompare(b.applicantString);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }

    if (field === "filingRepName") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.filingRepName.localeCompare(b.filingRepName);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }

    if (field === "projectManagerName") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.projectManagerName.localeCompare(b.projectManagerName);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }


    if (field === "fullAddress") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.fullAddress.localeCompare(b.fullAddress);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }

    if (field === "ownerString") {
      const sorted = [...allData].sort((a, b) => {
        const cmp = a.ownerString.localeCompare(b.ownerString);
        return dir === "asc" ? cmp : -cmp;
      });
      setClientSortedRows(sorted);
      return;
    }

    setClientSortedRows(null);
    updateParams({ sort: field, order: dir, page: 0 });
  };

  const userProfile = context?.state?.userProfile || {};

  useEffect(() => {
    if (collectionName === "contacts") {
      const updatedVisibilityModel = {
        ...columnVisibilityModel,
        ...(contactExtraFilters.architect && { architect: true }),
        ...(contactExtraFilters.client && { client: true }),
        ...(contactExtraFilters.contractor && { contractor: true }),
        ...(contactExtraFilters.employee && { employee: true }),
        ...(contactExtraFilters.engineer && { engineer: true }),
        ...(contactExtraFilters.filingRep && { filingRep: true }),
        ...(contactExtraFilters.owner && { owner: true }),
        ...(contactExtraFilters.tenant && { tenant: true }),

        ...(!contactExtraFilters.architect && { architect: false }),
        ...(!contactExtraFilters.client && { client: false }),
        ...(!contactExtraFilters.contractor && { contractor: false }),
        ...(!contactExtraFilters.employee && { employee: false }),
        ...(!contactExtraFilters.engineer && { engineer: false }),
        ...(!contactExtraFilters.filingRep && { filingRep: false }),
        ...(!contactExtraFilters.owner && { owner: false }),
        ...(!contactExtraFilters.tenant && { tenant: false })
      };

      setColumnVisibilityModel(updatedVisibilityModel);
    }
  }, [contactExtraFilters]);










  const columns = useMemo(
    () => [
      ...columnsRaw?.map((col) => ({
        headerClassName: "forms-header",
        flex: 1,
        sortable: !disableSorting,
        disableColumnMenu: disableColumnMenu,
        ...col,
      })),



      (collectionName == "invoices" && !isInactive && userProfile.subscription !== "inactive") && {
        field: "Generate",
        headerName: "Generate",
        sortable: false,
        // flex: 1,
        headerClassName: "forms-header",
        renderCell: ({ row }) => (
          <Grid item xs={1} md={1} lg={1}>
            <IconButton onClick={(e) => e.stopPropagation()}>
              <PrintInvoiceButton invoiceId={row.id} />
            </IconButton>

            <IconButton onClick={(e) => e.stopPropagation()}>
              <DownloadInvoiceButton invoiceId={row.id} />
            </IconButton>
          </Grid>
        )
      },

      (!hideActions && !isInactive && userProfile.subscription !== "inactive") && {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        // flex: 1,
        headerClassName: "forms-header",
        renderCell: ({ row }) => (
          <Grid item xs={1} md={1} lg={1}>
            <IconButton onClick={(e) => e.stopPropagation()}>
              <Delete
                onClick={() => handleDelete(row.id, row.customerReference, row)}
              />
            </IconButton>
          </Grid>
        ),
      },

      collectionName == "profiles" && {
        field: "2FA",
        headerName: "2FA",
        // flex: 1,
        headerClassName: "forms-header",
        renderCell: ({ row }) => (
          <Grid item xs={1} md={1} lg={1}>
            <IconButton onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={row.enforceTwoFactor ?? true}
                onChange={(e) => handle2FA(row, e.target.checked)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </IconButton>
          </Grid>
        ),
      },
    ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsRaw, disableSorting, disableColumnMenu]
  );


  const handleOpenForm = (id) => {
    if (collectionName == 'projects' && fromBuildingReferencesJobDetails?.fromBuildingReferencesJob) {
      navigate(`/${collectionName}/details/${id}`, { state: { fromBuildingReferencesJobDetails: fromBuildingReferencesJobDetails } });
    } else {
      navigate(`/${collectionName}/details/${id}`);
    }

  };

  const getGroupClass = (row) =>
    row?.userId !== context?.state?.userProfile?.userId &&
    row?.groupId === context?.state?.userProfile?.groupId;

  const pageSize = Number(params.get(`${tableId}_pageSize`)) ||
    Number(params.get("pageSize")) ||
    propPageSize || // Use the prop if provided
    columnsConfig?.defaultRowsLimit;

  const page = Number(params.get(`${tableId}_page`)) ||
    Number(params.get("page")) ||
    0;





  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel((prevState) => ({
      ...prevState,
      ...newModel, // Update only the columns that are changed
    }));
  };

  const handlePaginationChange = (params) => {
    if (data?.length === rowCount) {
      updateParams({
        [`${tableId}_page`]: params.page,
        [`${tableId}_pageSize`]: params.pageSize,
        // Keep backward compatibility for any components that might still use the old params
        page: false, // Remove old global params
        pageSize: false, // Remove old global params
      });
    }
  };
  const handleRowsPerPageChange = (e) => {
    updateParams({
      [`${tableId}_pageSize`]: `${e.target.value}`,
      [`${tableId}_page`]: 0, // Reset to first page when changing page size
      pageSize: false, // Remove old global param
      page: false, // Remove old global param
    });
  };

  const handlePageChange = (_, newPage) => {
    updateParams({
      [`${tableId}_page`]: newPage,
      [`${tableId}_pageIncreasing`]: `${newPage > page}`,
      page: false, // Remove old global param
    });
  };



  return (
    <div style={{ paddingBottom: "50px" }}>
      <DataGrid
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handlePaginationChange}
        disableColumnMenu={disableColumnMenu} // Pass to DataGrid
        sortingMode={disableSorting ? null : "server"}
        pagination={data?.length === rowCount}
        hideFooter={data?.length !== rowCount}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        sx={{ maxWidth: "98vw", overflow: "auto", marginTop: "20px" }}
        rows={clientSortedRows ?? data}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: columnsConfig?.[collectionName] || [],
          },
          sorting: {
            sortModel: [{ field: "billingDate", sort: "asc" }],
          },
        }}
        autoHeight={true}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={{ pageSize, page }}
        getRowClassName={(params) =>
          `table-row--${getGroupClass(params.row) ? "secondary" : "main"}`
        }
        onFilterModelChange={(e) => {
          updateParams({
            pageSize: false,
            page: false,
            search: e?.items?.map((item) => item?.value).join(""),
          });
        }}


        onRowClick={(e) => handleOpenForm(e?.row?.id)}
        slots={{
          toolbar: GridToolbar,
        }}
      />
      {data?.length !== rowCount && (
        <TablePagination
          key={`${rowCount}-${data.length}-${page}-${pageSize}`}
          component="div"
          count={rowCount}
          page={page}
          onPageChange={(_, e) => {
            updateParams({
              page: e,
              pageIncreasing: `${e > Number(params.get("page"))}`,
            });

          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            updateParams({ pageSize: `${e.target.value}`, page: false });
          }}
        />
      )}

    </div>
  );
}

export default memo(AdvancedTable);
